import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, Users as UsersIcon, KeyRound, Trash2, Ban, CheckCircle2, Eye, EyeOff, Pencil } from "lucide-react";
import { RolePermissions } from "@/components/RolePermissions";
import { usePermissions } from "@/context/PermissionsContext";
import { ReportRecipients } from "@/components/ReportRecipients";
import { ReportSchedule } from "@/components/ReportSchedule";

const EMPTY = { name: "", email: "", password: "", role: "distributor" };
const BASE_ROLE_LABELS = { newcomer: "Yeni Katılımcı", distributor: "Distribütör", tabteam: "Tab Team", admin: "Admin" };
const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);

export default function Users() {
  const { user: me } = useAuth();
  const { labels } = usePermissions();
  const ROLE_LABELS = { ...BASE_ROLE_LABELS, ...Object.fromEntries(Object.entries(labels || {}).filter(([k]) => !BASE_ROLE_LABELS[k])) };
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [pwTarget, setPwTarget] = useState(null);
  const [pwValue, setPwValue] = useState("");
  const [shown, setShown] = useState({});
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", password: "" });
  const openEdit = (u) => { setEditTarget(u); setEditForm({ name: u.name || "", email: u.email, password: "" }); };
  const saveEdit = async () => {
    if (!editForm.email.trim()) return toast.error("E-posta gerekli");
    if (editForm.password && editForm.password.length < 6) return toast.error("Şifre en az 6 karakter olmalı");
    const body = { name: editForm.name, email: editForm.email.trim() };
    if (editForm.password) body.password = editForm.password;
    await patch(editTarget, body, "Kullanıcı güncellendi");
    setEditTarget(null);
  };

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(err);
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.email.trim()) return toast.error("E-posta gerekli");
    if (form.password.length < 6) return toast.error("Şifre en az 6 karakter olmalı");
    try { await api.post("/users", form); toast.success("Kullanıcı oluşturuldu"); setOpen(false); setForm(EMPTY); load(); } catch (e) { err(e); }
  };
  const patch = async (u, body, msg) => { try { await api.patch(`/users/${u.id}`, body); toast.success(msg); load(); } catch (e) { err(e); } };
  const remove = async (u) => {
    if (!window.confirm(`${u.email} silinsin mi?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Kullanıcı silindi"); load(); } catch (e) { err(e); }
  };
  const setPassword = async () => {
    if (pwValue.length < 6) return toast.error("Şifre en az 6 karakter olmalı");
    await patch(pwTarget, { password: pwValue }, "Şifre güncellendi");
    setPwTarget(null); setPwValue("");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Kullanıcılar</h1>
          <p className="mt-1 text-sm text-muted-foreground">Personel hesapları ve yetkiler (Distribütör / Tab Team / Admin)</p>
        </div>
        <Button data-testid="add-user-btn" onClick={() => { setForm(EMPTY); setOpen(true); }} className="font-semibold"><UserPlus className="mr-1.5 h-4 w-4" /> Yeni Kullanıcı</Button>
      </div>

      <div className="rounded-xl border border-border bg-card" data-testid="users-list">
        {users.map((u) => (
          <div key={u.id} data-testid={`user-row-${u.id}`} className={`flex flex-wrap items-center gap-3 border-b border-border px-5 py-3 last:border-0 ${u.active ? "" : "opacity-60"}`}>
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary font-bold">{(u.name || u.email)[0].toUpperCase()}</div>
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">{u.name || "—"} {u.id === me?.id && <span className="text-xs text-muted-foreground">(siz)</span>}</p>
              <p className="truncate text-xs text-muted-foreground">{u.email}</p>
              <p className="flex items-center gap-1 font-mono text-xs text-muted-foreground" data-testid={`user-pw-plain-${u.id}`}>
                Şifre: <span className={shown[u.id] ? "text-foreground" : "tracking-widest"}>{shown[u.id] ? (u.password_plain || "—") : "••••••••"}</span>
                <button type="button" data-testid={`user-pw-show-${u.id}`} onClick={() => setShown({ ...shown, [u.id]: !shown[u.id] })} className="ml-1 hover:text-foreground">
                  {shown[u.id] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </button>
              </p>
            </div>
            <Select value={ROLE_LABELS[u.role] ? u.role : "distributor"} onValueChange={(v) => patch(u, { role: v }, "Rol güncellendi")} disabled={u.id === me?.id}>
              <SelectTrigger className="w-36" data-testid={`user-role-${u.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(ROLE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${u.active ? "bg-primary/15 text-primary" : "bg-destructive/15 text-destructive"}`} data-testid={`user-status-${u.id}`}>{u.active ? "Aktif" : "Pasif"}</span>
            {u.role === "newcomer" && (
              <Button size="sm" className="h-7" data-testid={`user-approve-${u.id}`} onClick={() => patch(u, { role: "distributor" }, "Kullanıcı onaylandı (Distribütör)")}>Onayla</Button>
            )}
            <div className="flex gap-1">
              <Button size="icon" variant="ghost" title="Bilgileri düzenle" data-testid={`user-edit-${u.id}`} onClick={() => openEdit(u)}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" title="Şifre belirle" data-testid={`user-pw-${u.id}`} onClick={() => { setPwTarget(u); setPwValue(""); }}><KeyRound className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" title={u.active ? "Devre dışı bırak" : "Aktif et"} data-testid={`user-toggle-${u.id}`} disabled={u.id === me?.id}
                onClick={() => patch(u, { active: !u.active }, u.active ? "Hesap devre dışı bırakıldı" : "Hesap aktif edildi")}>
                {u.active ? <Ban className="h-4 w-4 text-amber-500" /> : <CheckCircle2 className="h-4 w-4 text-primary" />}
              </Button>
              <Button size="icon" variant="ghost" title="Sil" data-testid={`user-delete-${u.id}`} disabled={u.id === me?.id} onClick={() => remove(u)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
            </div>
          </div>
        ))}
        {users.length === 0 && <p className="flex items-center gap-2 p-8 text-sm text-muted-foreground"><UsersIcon className="h-4 w-4" /> Kullanıcı yok</p>}
      </div>

      <RolePermissions />
      <ReportRecipients />
      <ReportSchedule />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader><DialogTitle>Yeni Kullanıcı</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Ad Soyad</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">E-posta</Label><Input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Şifre</Label><Input data-testid="user-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" /></div>
            <div>
              <Label className="text-xs">Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(ROLE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button data-testid="save-user-btn" onClick={create} className="font-semibold">Oluştur</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent data-testid="user-edit-dialog">
          <DialogHeader><DialogTitle>Kullanıcıyı Düzenle — {editTarget?.email}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Ad Soyad</Label><Input data-testid="user-edit-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">E-posta</Label><Input data-testid="user-edit-email" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Yeni Şifre (boş bırakılırsa değişmez)</Label><Input data-testid="user-edit-password" type="password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter><Button data-testid="user-edit-save" onClick={saveEdit} className="font-semibold">Kaydet</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwTarget} onOpenChange={(o) => !o && setPwTarget(null)}>
        <DialogContent data-testid="user-pw-dialog">
          <DialogHeader><DialogTitle>Şifre Belirle — {pwTarget?.email}</DialogTitle></DialogHeader>
          <div><Label className="text-xs">Yeni Şifre</Label><Input data-testid="user-pw-input" type="password" value={pwValue} onChange={(e) => setPwValue(e.target.value)} className="mt-1" autoFocus /></div>
          <DialogFooter><Button data-testid="user-pw-save" onClick={setPassword} className="font-semibold">Kaydet</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
